'use client';

import { useEffect } from 'react';
import { report } from '~/lib/exception-handler';
import { handleChunkLoadError } from '~/lib/chunk-load-error';

// Top-level error boundary. Catches errors that escape the root layout —
// including layout/template render failures. Must be a client component and
// must render its own <html>/<body>, since the root layout is the thing that
// just crashed.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // #766 — a chunk that vanished under a deploy is fixed by one reload, not
    // by an incident. Guarded against loops in handleChunkLoadError.
    if (handleChunkLoadError(error)) return;
    report(error, {
      filename: 'app/global-error.tsx',
      method: 'GlobalError',
      tags: error.digest ? { digest: error.digest } : undefined,
    });
  }, [error]);

  return (
    <html>
      <body className="flex min-h-screen items-center justify-center bg-white text-center font-sans">
        <div className="max-w-md space-y-4 p-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Something went wrong
          </h1>
          <p className="text-sm text-gray-600">
            We&apos;ve been notified. You can try again or head back to the home
            page.
          </p>
          <div className="flex justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={reset}
              className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Try again
            </button>
            <a
              href="/"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              Go home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
